import * as XLSX from 'xlsx';
import { notification } from 'antd';

/**
 * Cleans numeric values by removing unit strings (mm, ", inches, in, cm, etc.)
 * Preserves decimal values and returns only the numeric part
 * @param {string} value - The value to clean
 * @returns {string} - Cleaned numeric value as string
 */
const cleanNumericValue = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Trim whitespace
  let cleaned = value.trim();

  if (cleaned === '') {
    return '';
  }

  // Remove quotes from beginning and end (both straight " and curly ")
  cleaned = cleaned.replace(/^[""]+|[""]+$/g, '').trim();

  // Remove common unit strings (case-insensitive) with optional spaces
  // Units to remove: mm, ", inches, inch, in, cm, m (meter), ft, feet
  // Handle both with and without spaces: "18.26mm", "18.26 mm", "6.625"", "6.625 """, etc.
  cleaned = cleaned
    .replace(/\s*mm\s*$/i, '') // Remove "mm" at the end
    .replace(/\s*[""]\s*$/g, '') // Remove " or " at the end (both straight and curly quotes)
    .replace(/\s*inches?\s*$/i, '') // Remove "inches" or "inch" at the end
    .replace(/\s*in\s*$/i, '') // Remove "in" at the end (but not if part of a number like "18.26")
    .replace(/\s*cm\s*$/i, '') // Remove "cm" at the end
    .replace(/\s*m\s*$/i, '') // Remove "m" at the end (meter)
    .replace(/\s*ft\s*$/i, '') // Remove "ft" at the end
    .replace(/\s*feet\s*$/i, '') // Remove "feet" at the end
    .trim();

  // Extract numeric value (including decimals and negative signs)
  // This regex matches: optional negative sign, digits, optional decimal point and more digits
  const numericMatch = cleaned.match(/^-?\d+\.?\d*$/);

  if (numericMatch) {
    return numericMatch[0];
  }

  // If no clean numeric match, try to extract just the number part
  // This handles cases where there might be extra characters
  const numberExtract = cleaned.match(/-?\d+\.?\d*/);
  if (numberExtract) {
    return numberExtract[0];
  }

  // If no numeric value found, return empty string
  return '';
};

/**
 * Parses CSV or Excel file and extracts ID, joint_reference, pipe_diameter, and wall_thickness
 * @param {File} file - The CSV or Excel file to parse
 * @returns {Promise<{success: boolean, data: Array, errors: Array}>}
 */
export const parseCsvUpdateFile = async (file) => {
  try {
    // Validate file type
    const fileName = file.name.toLowerCase();
    const isValidFile =
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls');

    if (!isValidFile) {
      return {
        success: false,
        data: [],
        errors: ['Invalid file type. Please upload a CSV or Excel file (.csv, .xlsx, .xls)'],
      };
    }

    // Read file as array buffer
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON with header row
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (!jsonData || jsonData.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['File is empty or has no data rows'],
      };
    }

    // Get headers (first row)
    const headers = jsonData[0].map((h) => String(h || '').trim());

    // Find column indices (case-insensitive)
    const findColumnIndex = (columnNames) => {
      for (const name of columnNames) {
        const index = headers.findIndex(
          (h) => h.toLowerCase() === name.toLowerCase()
        );
        if (index !== -1) return index;
      }
      return -1;
    };

    const idIndex = findColumnIndex(['ID', 'id', 'Id']);
    const jointRefIndex = findColumnIndex([
      'joint_reference',
      'JointReference',
      'joint reference',
      'Joint Reference',
    ]);
    const pipeDiaIndex = findColumnIndex([
      'pipe_diameter',
      'PipeDiameter',
      'pipe diameter',
      'Pipe Diameter',
    ]);
    const wallThickIndex = findColumnIndex([
      'wall_thickness',
      'WallThickness',
      'wall thickness',
      'Wall Thickness',
    ]);

    // Validate ID column exists
    if (idIndex === -1) {
      return {
        success: false,
        data: [],
        errors: ['ID column not found in file. Please ensure your file has an ID column.'],
      };
    }

    const errors = [];
    const parsedData = [];

    // Process data rows (skip header row)
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      const id = row[idIndex];

      // Skip empty rows
      if (!id && !row.some((cell) => cell !== null && cell !== undefined && cell !== '')) {
        continue;
      }

      // ID is required
      if (!id || String(id).trim() === '') {
        errors.push(`Row ${i + 1}: Missing ID value`);
        continue;
      }

      // Extract raw values
      const rawPipeDiameter = pipeDiaIndex !== -1 && row[pipeDiaIndex] !== undefined && row[pipeDiaIndex] !== null
        ? String(row[pipeDiaIndex]).trim()
        : '';
      const rawWallThickness = wallThickIndex !== -1 && row[wallThickIndex] !== undefined && row[wallThickIndex] !== null
        ? String(row[wallThickIndex]).trim()
        : '';

      const record = {
        ID: String(id).trim(),
        JointReference: jointRefIndex !== -1 && row[jointRefIndex] !== undefined && row[jointRefIndex] !== null
          ? String(row[jointRefIndex]).trim()
          : '',
        PipeDiameter: cleanNumericValue(rawPipeDiameter),
        WallThickness: cleanNumericValue(rawWallThickness),
      };

      parsedData.push(record);
    }

    if (parsedData.length === 0) {
      return {
        success: false,
        data: [],
        errors: ['No valid data rows found in file'],
      };
    }

    // Show warnings for missing optional columns
    if (jointRefIndex === -1) {
      errors.push('Warning: joint_reference column not found');
    }
    if (pipeDiaIndex === -1) {
      errors.push('Warning: pipe_diameter column not found');
    }
    if (wallThickIndex === -1) {
      errors.push('Warning: wall_thickness column not found');
    }

    return {
      success: true,
      data: parsedData,
      errors: errors.length > 0 ? errors : [],
    };
  } catch (error) {
    console.error('Error parsing file:', error);
    return {
      success: false,
      data: [],
      errors: [`Error parsing file: ${error.message}`],
    };
  }
};
